import { useState, type FormEvent } from 'react';
import { gql, useMutation } from '@apollo/client';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const ADD_NOTE = gql`
  mutation AddCaseConferencingNote($odrnumber: String!, $participant: String!, $note: AddCoordinatedCareNoteInput!) {
    addCoordinatedCareNote(odrnumber: $odrnumber, participant: $participant, note: $note) { message }
  }
`;

const SERVICE_TYPES = ['Housing', 'Community', 'Pre-release', 'Psychiatry', 'Nursing'];
const PROCEDURE_LENGTHS = [15, 30, 45, 60, 75, 90, 105, 120, 150, 180, 210, 240];

interface NoteInput {
  service_type: string;
  procedurelength: string;
  note_entry_date: string;
  procedurenote: string;
}

interface NoteFormProps {
  participant: number;
  odrparticipant: number;
  enteredBy: string;
  onSaved: () => void;
  onCancel: () => void;
  onSavingChange?: (saving: boolean) => void;
}

export const CaseConferencingNoteForm = ({ participant, odrparticipant, enteredBy, onSaved, onCancel, onSavingChange }: NoteFormProps) => {
  const [note, setNote] = useState<NoteInput>({ service_type: '', procedurelength: '', note_entry_date: format(new Date(), 'yyyy-MM-dd'), procedurenote: '' });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [addNote] = useMutation<{ addCoordinatedCareNote: { message: string | null } | null }, { odrnumber: string; participant: string; note: NoteInput }>(ADD_NOTE);
  const update = (key: keyof NoteInput, value: string) => setNote((previous) => ({ ...previous, [key]: value }));

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (saving) return;
    if (!note.procedurenote.trim() || !note.note_entry_date || !PROCEDURE_LENGTHS.includes(Number(note.procedurelength))) {
      setError('Enter a procedure note, service date, and procedure length.');
      return;
    }
    setSaving(true);
    onSavingChange?.(true);
    setError(null);
    try {
      const result = await addNote({ variables: { participant: String(participant), odrnumber: String(odrparticipant), note } });
      if (result.errors?.length || result.data?.addCoordinatedCareNote?.message !== 'Coordinated Care note added successfully') {
        throw new Error('Note save was not confirmed');
      }
      onSaved();
    } catch {
      setError('Unable to save coordinated care note. Please try again.');
    } finally {
      setSaving(false);
      onSavingChange?.(false);
    }
  };

  return (
    <form aria-label="Add coordinated care note" onSubmit={submit} className="space-y-3">
      <h2 className="font-semibold">Add New Coordinated Care Note</h2>
      <Label htmlFor="case-conferencing-note-service">Service Type</Label>
      <Select value={note.service_type} onValueChange={(value) => update('service_type', value)} disabled={saving}>
        <SelectTrigger id="case-conferencing-note-service"><SelectValue placeholder="Select service type" /></SelectTrigger>
        <SelectContent>{SERVICE_TYPES.map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}</SelectContent>
      </Select>
      <Label htmlFor="case-conferencing-note-length">Procedure Length</Label>
      <Select value={note.procedurelength} onValueChange={(value) => update('procedurelength', value)} disabled={saving}>
        <SelectTrigger id="case-conferencing-note-length"><SelectValue placeholder="Select procedure length" /></SelectTrigger>
        <SelectContent>{PROCEDURE_LENGTHS.map((value) => <SelectItem key={value} value={String(value)}>{value} mins</SelectItem>)}</SelectContent>
      </Select>
      <Label htmlFor="case-conferencing-note-date">Service Date</Label>
      <Input id="case-conferencing-note-date" type="date" value={note.note_entry_date} onChange={(event) => update('note_entry_date', event.target.value)} disabled={saving} required />
      <Label htmlFor="case-conferencing-note-actor">Entered By</Label>
      <Input id="case-conferencing-note-actor" value={enteredBy} readOnly />
      <Label htmlFor="case-conferencing-note-text">Procedure Note</Label>
      <Textarea id="case-conferencing-note-text" value={note.procedurenote} onChange={(event) => update('procedurenote', event.target.value)} disabled={saving} required />
      {error && <p role="alert">{error}</p>}
      <div className="flex gap-2">
        <Button id="case-conferencing-note-save" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save Note'}</Button>
        <Button id="case-conferencing-note-cancel" type="button" variant="outline" disabled={saving} onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
};
